import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpResponse, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import * as _ from 'lodash';

import { Config } from '../config/env.config';
import { AuthService } from '../auth/auth.service';
import { BaseHttpService } from '../base/_base-http.service';
import { contentHeaders } from 'common/headers';
import { monthlyPlans } from './subscription.stub';
import {catchError} from 'rxjs/operators';


@Injectable()
export class SubscriptionService extends BaseHttpService {

  private manageBilling: boolean = false;
  private subscription: {} = null;
  private accountBrand: {} = null;

  /*******************************************
   * SERVICE GETTERS
   *******************************************/

  get controllerPrefix(): string {
    return this.manageBilling ? '/manage-billing' : '';
  }

  /*******************************************
   * SERVICE PUBLIC METHODS
   *******************************************/

  public getSubscription(brandId: string): Observable<any> {
    return new Observable(observer => {
      if (this.subscription && !_.isString(this.subscription)) { // not error string
        observer.next(this.subscription);
        return observer.complete();
      } else {
        this.http.get(`${Config.API}${this.controllerPrefix}/subscription/${brandId}`)

          .pipe(catchError(this.handleError.bind(this)))
          .subscribe((subscription: any) => {
            this.storeSubscription(subscription);
            observer.next(this.subscription);
            observer.complete();
          });
      }
    });
  }

  public getPlanItemsCount(brandId: string): Observable<any> {
    return this.http.get(`${Config.API}${this.controllerPrefix}/subscription/items-count/${brandId}`)

      .pipe(catchError(this.handleError.bind(this)));
  }

  public getNextPlanInfo(brandId: string): Observable<any> {
    return this.http.get(`${Config.API}/next-plan-info/${brandId}`)

        .pipe(catchError(this.handleError.bind(this)));
  }

  public getUsersCountInfo(brandId: string): Observable<any> {
    return this.http.get(`${Config.API}${this.controllerPrefix}/subscription/users-count/${brandId}`)

        .pipe(catchError(this.handleError.bind(this)));
  }

  public getAddonInfo(brandId: string): Observable<any> {
    return this.http.get(`${Config.API}${this.controllerPrefix}/subscription/add-ons/${brandId}`)

        .pipe(catchError(this.handleError.bind(this)));
  }

  public getMonthlyPlansInfo(brandId: string): Observable<any> {
    return this.http.get(`${Config.API}${this.controllerPrefix}/subscription/plans/${brandId}`)

        .pipe(catchError(this.handleError.bind(this)));
  }

  public getAccountBrand(brandId: string): Observable<any> {
    return new Observable(observer => {
      if (this.accountBrand && !_.isString(this.accountBrand)) { // not error string
        observer.next(this.accountBrand);
        return observer.complete();
      } else {
        this.http.get(`${Config.API}/account-brand/${brandId}`)
            .pipe(catchError(this.handleError.bind(this)))
          .subscribe((accountBrand: any) => {
            if (accountBrand.id) {
              this.storeAccountBrand(accountBrand);
            }
            observer.next(this.accountBrand);
            observer.complete();
          });
      }
    });
  }

  public getCoupon(brandId: string): Observable<any> {
    return this.http.get(`${Config.API}${this.controllerPrefix}/coupon/${brandId}`)

        .pipe(catchError(this.handleError.bind(this)));
  }

  public getDiscount(brandId: string): Observable<any> {
    return this.http.get(`${Config.API}${this.controllerPrefix}/discount/${brandId}`)

        .pipe(catchError(this.handleError.bind(this)));
  }

  // this is for subscription-unguard, move it when API is ready
  getBrandInfoInvite(token: string) {
    console.log(`${Config.API}/billing-invite/${token}`);
    return of({
      brand_name: 'Hoodies Test',
      brand_id: 14265,
      brand_logo: 'https://stagepim.rxspeed.com:8443/brand-logo/14265?type=square_logo',
    });
  }

  public storeAccountBrand(accountBrand: {}): void {
    this.accountBrand = accountBrand;
  }

  public clearStoredAccountBrand() {
    this.accountBrand = null;
  }

  public storeSubscription(subscription: {}): void {
    this.subscription = subscription;
  }

  public clearStoredSubscription() {
    this.subscription = null;
  }

  public startSubscription(
    brandId: string, subscriptionData: any,
  ): Observable<any> {

    const data: any = {};

    // @ts-ignore
      _.mapKeys(subscriptionData, (value: any, key: any) => {
      switch (key) {
        case 'planId':
          data.base_subscription = {
            id: subscriptionData[key],
          };
          break;
        case 'maxUsersCount':
          data.add_ons = [{
            id: 'user-addon',
            quantity: subscriptionData[key],
          }];
          break;
        default:
          data[key] = subscriptionData[key];
          break;
      }
    });

    const body = JSON.stringify(data);

    this.clearStoredSubscription();

    return this.http.post(
        `${Config.API}${this.controllerPrefix}/subscription/${brandId}`,
        body,
        {headers: contentHeaders},
      )

        .pipe(catchError(this.handleError.bind(this)));
  }

  public updateSubscription(
    brandId: string, subscriptionData: any,
  ): Observable<any> {

    const data: any = {};

    // @ts-ignore
      _.mapKeys(subscriptionData, (value: any, key: any) => {
      switch (key) {
        case 'planId':
          data.base_subscription = {
            id: subscriptionData[key],
          };
          break;
        case 'maxUsersCount':
          data.add_ons = [{
            id: 'user-addon',
            quantity: subscriptionData[key],
          }];
          break;
        default:
          data[key] = subscriptionData[key];
          break;
      }
    });

    const body = JSON.stringify(data);

    this.clearStoredSubscription();

    return this.http.put(
        `${Config.API}${this.controllerPrefix}/subscription/${brandId}`,
        body,
        {headers: contentHeaders},
      )

        .pipe(catchError(this.handleError.bind(this)));
  }

  public updateCard(brandId: string, token: string): Observable<any> {
    const body = JSON.stringify({token: token});
    return this.http.put(
        `${Config.API}${this.controllerPrefix}/subscription/update-card/${brandId}`,
        body,
        {headers: contentHeaders},
      )

        .pipe(catchError(this.handleError.bind(this)));
  }

  public setManageBilling(manageBilling: boolean) {
    this.manageBilling = manageBilling;
  }

  // manage billing method for post invite
  public postInvite(brandId: string, emailsTo: string,
    emailsCC: string, emailsBCC: string,
    emailSubject: string, emailBody: string) {

    const body = JSON.stringify({
      email_to: emailsTo,
      email_cc: emailsCC,
      email_bcc: emailsBCC,
      email_body: emailBody,
      email_subject: emailSubject,
    });

    return this.http.post(
        `${Config.API}/manage-billing/${brandId}/invite`,
        body,
        {headers: contentHeaders},
      )

        .pipe(catchError(this.handleError.bind(this)));
  }

  // manage billing method for check token
  public getTokenData(token: string) {
    return this.http.get(`${Config.API}/manage-billing/invite?token=${token}`)

        .pipe(catchError(this.handleManageBillingError.bind(this)));
  }

  // manage billing method for complete flow
  public putManageBillingComplete(brandId: string) {
    return this.http.put(
      `${Config.API}/manage-billing/${brandId}/invite/complete`,
        {},
        {headers: contentHeaders},
      )

        .pipe(catchError(this.handleManageBillingError.bind(this)));
  }

  private handleManageBillingError(err: any) {
    let errMessage: string;
    if (err instanceof HttpErrorResponse) {
        if (err.error instanceof Error) {
            // A client-side or network error occurred. Handle it accordingly.
            errMessage = err.error.message;
        } else {
            // The backend returned an unsuccessful response code.
            errMessage = `${err.status} - ${err.statusText} || ''} ${err.error}`;
        }
    } else {
      errMessage = err.message ? err.message : err.toString();
    }
    return of(errMessage);
  }

  public getStripeManageBillingUrl(brandId: string) {
    return this.http.get(`${Config.API}/manage-billing/subscription/stripe-payment-methods/${brandId}`)
      .pipe(catchError(this.handleError.bind(this)));
  }
}
